import Dexie from 'dexie';

/**
 * FleurDeVieDB - Local database mirroring MongoDB backend collections.
 * Uses Dexie.js (IndexedDB wrapper) for offline-first architecture.
 * 
 * Index format: 'primaryKey, index1, index2, [compound+index]'
 * ++ = auto-increment, & = unique
 *
 * v2: Added updatedAt to pendingActions for last-write-wins sync.
 */
const db = new Dexie('FleurDeVieDB');

db.version(1).stores({
  users: 'id, email, phone',
  cycles: 'id, userId',
  cycleHistory: 'id, userId, periodStart',
  contraceptions: 'id, userId',
  contraceptionLogs: 'id, userId, contraceptionId, date',
  modules: 'id, category',
  lessons: 'id, moduleId, order',
  quizzes: 'id, moduleId, lessonId',
  quizSubmissions: 'id, userId, quizId',
  userProgress: 'id, userId, moduleId, [userId+moduleId]',
  notifications: 'id, userId, type, read',
  reminders: 'id, userId, type',
  badges: 'id',
  leaderboard: 'id, rank',
  weeklyChallenges: 'id',
  rewards: 'id',
  girlsRoomEvents: 'id, city, date',
  girlsRoomParticipations: 'id, userId, eventId',
  communityQuestions: 'id, category, status',
  tips: 'id, category, type',
  tipFavorites: 'id, userId, tipId',
  partners: 'id',
  alertSettings: 'userId',
  customAlerts: 'id, userId',
  syncMeta: 'key',
  pendingActions: '++id, type, endpoint, queuedAt',
});

// v2: Add updatedAt index to pendingActions for LWW conflict resolution
db.version(2).stores({
  pendingActions: '++id, type, endpoint, queuedAt, updatedAt',
}).upgrade(tx => {
  // Set updatedAt = queuedAt for any existing pending actions
  return tx.table('pendingActions').toCollection().modify(action => {
    if (!action.updatedAt) {
      action.updatedAt = action.queuedAt || new Date().toISOString();
    }
  });
});

export default db;

// ============== HELPER: Bulk upsert ==============
/**
 * Insert or replace an array of documents into a Dexie table.
 * Expects each item to have an `id` field.
 */
export async function bulkUpsert(table, items) {
  if (!items || items.length === 0) return;
  await table.bulkPut(items);
}

// ============== HELPER: Get sync timestamp ==============
export async function getLastSync(collectionName) {
  const meta = await db.syncMeta.get(collectionName);
  return meta?.syncedAt || null;
}

export async function setLastSync(collectionName) {
  await db.syncMeta.put({ key: collectionName, syncedAt: new Date().toISOString() });
}

// ============== HELPER: Clear all local data ==============
export async function clearAllLocalData() {
  await Promise.all([
    db.users.clear(),
    db.cycles.clear(),
    db.cycleHistory.clear(),
    db.contraceptions.clear(),
    db.contraceptionLogs.clear(),
    db.modules.clear(),
    db.lessons.clear(),
    db.quizzes.clear(),
    db.quizSubmissions.clear(),
    db.userProgress.clear(),
    db.notifications.clear(),
    db.reminders.clear(),
    db.badges.clear(),
    db.leaderboard.clear(),
    db.weeklyChallenges.clear(),
    db.rewards.clear(),
    db.girlsRoomEvents.clear(),
    db.girlsRoomParticipations.clear(),
    db.communityQuestions.clear(),
    db.tips.clear(),
    db.tipFavorites.clear(),
    db.partners.clear(),
    db.alertSettings.clear(),
    db.customAlerts.clear(),
    db.syncMeta.clear(),
    db.pendingActions.clear(),
  ]);
}
